import { Injectable, Injector, isDevMode } from '@angular/core';
import { DatePipe, CurrencyPipe, PercentPipe, DecimalPipe, UpperCasePipe, LowerCasePipe } from '@angular/common';
import { CustomerHasPlanPipe } from '../../ng2/shared/pipes/customer-has-plan/customer-has-plan.pipe';
import { OrderByPipe } from '../../ng2/shared/pipes/order-by/order-by.pipe';
import { GetUserNamePipe } from '../../ng2/shared/pipes/get-user-name/get-user-name.pipe';
import { AccessibilityHasGuidelineIdPipe } from './accessibility/has-guideline-id.pipe';
import { AccessibilityHasGuidelinePipe } from './accessibility/has-guideline.pipe';
import { AccessibilityCloseCaptionPipe } from './accessibility/close-caption.pipe';
import { IsDefinedPipe } from '@client/app/filters/common/is-defined.pipe';
import { IsFunctionPipe } from '@client/app/filters/common/is-function.pipe';
import { IsNumberPipe } from '@client/app/filters/common/is-number.pipe';
import { SearchFieldPipe } from '@client/app/filters/common/search-field.pipe';
import { IsStatsSampleDataPipe } from '@client/app/filters/is-stats-dumy-data.pipe';
import { UptimeIncidientTimePipe } from '@client/app/filters/uptime-incidient-time.pipe';
import { MonSetMyTimezonePipe } from '@client/app/filters/set-to-my-timezone.pipe';
import { MonCharacterLimitPipe } from '@client/app/filters/character-limit.pipe';
import { HasDomainAccessPipe } from '@client/app/filters/has-domain-access.pipe';
import { FullUrlPipe } from '@client/app/filters/full-url.pipe';
import { TranslateWithPluralPipe } from '@client/app/filters/translate-with-plural.pipe';
import { MonNumberPipe } from '@client/app/filters/mon-number.pipe';
import { TranslateReadabilityPipe } from '@client/app/filters/translate-readability.pipe';
import { DomainActiveFeaturePipe } from '@client/app/filters/domain-active-feature.pipe';
import { E2eAttributeEncodingPipe } from '@client/app/filters/encode-uri.pipe';
import { UnderscorelessPipe } from '@client/app/filters/underscoreless.pipe';
import { MonIconsPipe } from '@client/app/filters/mon-icons.pipe';
import { TranslateSeoIssuePipe } from '@client/app/filters/translate-seo-issue.pipe';
import { WcagClassPipe } from '@client/app/filters/wcag-class.pipe';
import { HasAccessPipe } from '@client/app/filters/has-access.pipe';
import { RolePipe } from '@client/app/filters/role.pipe';
import { ActiveFeaturePipe } from '@monsido/core/pipes/active-feature/active-feature.pipe';
import { InverseColorCodePipe } from '@client/app/filters/inverse-color-code.pipe';
import { CapitalizePipe } from '@client/app/filters/capitalize.pipe';
import { EscapeHtmlPipe } from '@client/app/filters/escape-html.pipe';
import { EscapeRegExpPipe } from '@client/app/filters/escape-reg-exp.pipe';
import { GenerateUrlParamsPipe } from '@client/app/filters/generate-url-params.pipe';
import { MonRemoveWhiteSpacePipe } from '@client/app/filters/mon-remove-white-space.pipe';
import { ReturnHostnamePipe } from '@client/app/filters/return-hostname.pipe';
import { UserDatePipe } from '@client/app/filters/user-date.pipe';
import { TranslateLanguagePipe } from '@client/app/filters/translate-language.pipe';
import { IsUsingDemoDataPipe } from '@client/app/filters/is-using-demo-data.pipe';
import { HasDemoAccessPipe } from '@client/app/filters/has-demo-access.pipe';
import { PageFixSettingsPipe } from '@client/app/filters/page-fix-settings.pipe';
import { FrequencyTranslationPipe } from '@client/app/modules/page-performance/pipes/frequency-translation.pipe';
import { SearchNativePipe } from '@client/app/filters/common/search-native.pipe';

/**
 * Interface for pipe token mapping
 */
interface PipeTokenMap {
    [key: string]: any;
}

/**
 * Service that provides pipe transformation functionality for AngularJS filters.
 * This service can dynamically resolve and instantiate Angular pipes to be used
 * in AngularJS templates through the pipeTransformation filter.
 *
 * @example
 * // In AngularJS template:
 * {{ someDate | pipeTransformation:'date':'short' }}
 * {{ price | pipeTransformation:'currency':'USD':'symbol':'1.2-2' }}
 */
@Injectable({
    providedIn: 'root',
})
export class PipeService {
    private pipeCache = new Map<string, any>();

    /**
     * Map of pipe names to their corresponding Angular pipe classes
     */
    private pipeTokenMap: PipeTokenMap = {
        // Built-in Angular pipes
        'date': DatePipe,
        'currency': CurrencyPipe,
        'percent': PercentPipe,
        'decimal': DecimalPipe,
        'number': DecimalPipe, // Alias for decimal
        'uppercase': UpperCasePipe,
        'lowercase': LowerCasePipe,

        // Custom project pipes
        'monCustomerHasPlan': CustomerHasPlanPipe,
        'orderBy': OrderByPipe,
        'fullUrl': FullUrlPipe,
        'getUserName': GetUserNamePipe,
        'accessibilityHasGuidelineId': AccessibilityHasGuidelineIdPipe,
        'accessibilityHasGuideline': AccessibilityHasGuidelinePipe,
        'accessibilityCloseCaption': AccessibilityCloseCaptionPipe,
        'isDefined': IsDefinedPipe,
        'isFunction': IsFunctionPipe,
        'isNumber': IsNumberPipe,
        'searchField': SearchFieldPipe,
        'isStatsSampleData': IsStatsSampleDataPipe,
        'uptimeIncidientTime': UptimeIncidientTimePipe,
        'monSetMyTimezone': MonSetMyTimezonePipe,
        'monCharacterLimit': MonCharacterLimitPipe,
        'hasDomainAccess': HasDomainAccessPipe,
        'translateWithPlural': TranslateWithPluralPipe,
        'monNumber': MonNumberPipe,
        'translateReadability': TranslateReadabilityPipe,
        'domainActiveFeature': DomainActiveFeaturePipe,
        'e2eAttributeEncoding': E2eAttributeEncodingPipe,
        'underscoreless': UnderscorelessPipe,
        'monIcons': MonIconsPipe,
        'translateSeoIssue': TranslateSeoIssuePipe,
        'wcagClass': WcagClassPipe,
        'hasAccess': HasAccessPipe,
        'role': RolePipe,
        'activeFeature': ActiveFeaturePipe,
        'inverseColorCode': InverseColorCodePipe,
        'capitalize': CapitalizePipe,
        'escapeHtml': EscapeHtmlPipe,
        'escapeRegExp': EscapeRegExpPipe,
        'generateUrlParams': GenerateUrlParamsPipe,
        'monRemoveWhiteSpace': MonRemoveWhiteSpacePipe,
        'returnHostname': ReturnHostnamePipe,
        'userDate': UserDatePipe,
        'translateLanguage': TranslateLanguagePipe,
        'isUsingDemoData': IsUsingDemoDataPipe,
        'hasDemoAccess': HasDemoAccessPipe,
        'pageFixSettings': PageFixSettingsPipe,
        'frequencyTranslation': FrequencyTranslationPipe,
        'searchNative': SearchNativePipe,
    };

    constructor (private injector: Injector) {}

    /**
     * Gets the transformation function for a given pipe name.
     * Implements caching for performance optimization.
     *
     * @param pipeName - The name of the pipe to get the transformation for
     * @returns The pipe transformation function or null if pipe not found
     */
    get (pipeName: string): ((value: any, ...args: any[]) => any) | any {
        if (!pipeName || typeof pipeName !== 'string') {
            if (isDevMode()) {
                // eslint-disable-next-line no-console
                console.warn('[PipeTransformationService] Invalid pipe name provided:', pipeName);
            }
            return null;
        }

        // Check cache first
        if (this.pipeCache.has(pipeName)) {
            return this.pipeCache.get(pipeName);
        }

        try {
            // Get pipe class from token map
            const PipeClass = this.pipeTokenMap[pipeName];

            if (!PipeClass) {
                if (isDevMode()) {
                    // eslint-disable-next-line no-console
                    console.warn(`[PipeTransformationService] Pipe '${pipeName}' not found. Available pipes:`, Object.keys(this.pipeTokenMap));
                }
                return null;
            }

            // Try to get pipe instance from Angular injector
            let pipeInstance;
            try {
                pipeInstance = this.injector.get(PipeClass);
            } catch (injectorError) {
                // If injection fails, try to create instance manually
                try {
                    pipeInstance = new PipeClass();
                } catch (constructorError) {
                    if (isDevMode()) {
                        // eslint-disable-next-line no-console
                        console.error(`[PipeTransformationService] Failed to instantiate pipe '${pipeName}':`, constructorError);
                    }
                    return null;
                }
            }

            // Create transformation function
            const transformFunction = (value: any, ...args: any[]) => {
                try {
                    return pipeInstance.transform(value, ...args);
                } catch (transformError) {
                    if (isDevMode()) {
                        // eslint-disable-next-line no-console
                        console.error(`[PipeTransformationService] Error transforming value with pipe '${pipeName}':`, transformError);
                    }
                    return value; // Return original value on error
                }
            };

            // Cache the transformation function
            this.pipeCache.set(pipeName, transformFunction);

            return transformFunction;

        } catch (error) {
            if (isDevMode()) {
                // eslint-disable-next-line no-console
                console.error(`[PipeTransformationService] Unexpected error getting pipe '${pipeName}':`, error);
            }
            return null;
        }
    }

    /**
     * Clears the pipe cache. Useful for testing or memory management.
     */
    clearCache (): void {
        this.pipeCache.clear();
    }

    /**
     * Gets the list of available pipe names.
     *
     * @returns Array of available pipe names
     */
    getAvailablePipes (): string[] {
        return Object.keys(this.pipeTokenMap);
    }
}
